import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { toNumber } from 'lodash-es'
import { wallSettingsOf } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore, useModelStore } from '@editorStores'
import { useBlockScene, useResultsInvalidation, useSceneElement } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { createWall } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { useDisableIFCSelection } from 'src/state/hooks'
import WallDrawer2D from '../../StructuralPlanning/components/FloorplanDrawer/components/WallDrawer2D'

/**
 * Wires the "Wände (2D)" drawing tool into the IFC import scene by injecting Structural Planning's
 * `WallDrawer2D` while a storey is shown in the orthographic (2D) view. Unlike Structural Planning
 * — which adds a local wall and lets the user save it — a drawn wall is persisted right away via
 * `createWall`, so it becomes a normal server wall. IFC/GLB model selection is suppressed while
 * drawing so canvas clicks are dedicated to the drawer.
 *
 * `active` is whether the tool is switched on (the page passes `ifcMode === 'draw-walls'`, the
 * guided wall edit step its own draw-tool state); the storey/orthographic requirement is handled
 * here. `placement` is the placement drawn walls are created with (the guided step lets the user
 * pick it).
 */
const useIFCImportWallDrawing = (active: boolean, placement: WallPlacement = 'Internal'): void => {
  const { projectId }: { projectId?: string } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const storey = useControlStore(state => state.floorplanState.storey)
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const addWall = useModelStore(state => state.addWall)
  const removeWall = useModelStore(state => state.removeWall)
  const updateWall = useModelStore(state => state.updateWall)

  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const enabled = active && !!storey && isOrthographic

  // Keep the IFC/GLB model non-selectable while drawing so clicks are dedicated to the drawer.
  useDisableIFCSelection(enabled)

  const { mutate: createWallMutation, isLoading } = useMutation(
    (wall: Wall) =>
      createWall.request(projectId as string, {
        guid: wall.guid,
        p1: { x: wall.shape.points[0].x, y: wall.shape.points[0].y },
        p2: { x: wall.shape.points[1].x, y: wall.shape.points[1].y },
        storey_idx: toNumber(wall.storey),
        placement,
        ...wallSettingsOf(wall),
      }),
    {
      mutationKey: ['createWall', projectId],
      meta: {
        title: 'Wand erstellen',
        description: 'Erstellt eine gezeichnete Wand im Planarmodell.',
      },
      // Optimistically add the drawn wall to the planar model so it stays visible while the request
      // is in flight; drop it again if the request fails.
      onMutate: (wall: Wall) => {
        addWall({ ...wall, is_local: false })
      },
      onSuccess: async (created: ShapeObject) => {
        updateWall(created)
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
      },
      onError: (error: AxiosError, wall: Wall) => {
        removeWall(wall.guid)
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erstellen der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  // Block the scene while the wall is being created so no other interaction races the request.
  useBlockScene(isLoading)

  const onWallDrawn = useCallback((wall: Wall) => createWallMutation(wall), [createWallMutation])

  const properties = useMemo(
    () => ({ activeStorey: storey ?? '', onWallDrawn }),
    [storey, onWallDrawn],
  )

  useSceneElement({
    enabled,
    properties,
    component: WallDrawer2D,
  })
}

export default useIFCImportWallDrawing
